"use client";

import * as Dialog from "@radix-ui/react-dialog";
import { usePathname } from "next/navigation";
import { useEffect, useState, useMemo } from "react";

const STORAGE_KEY = "translation-guide-dismissed";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type Platform = "mobile" | "desktop";
type Browser =
  | "chrome"
  | "safari"
  | "firefox"
  | "edge"
  | "opera"
  | "samsung"
  | "unknown";

function detectPlatform(): Platform {
  if (typeof window === "undefined") return "desktop";

  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent,
    );

  return isMobile ? "mobile" : "desktop";
}

// 検索エンジン / SNS プレビューのクローラー。放置すると翻訳案内が開き
// Search Console のスクショでページ本文が隠れる。URL 検査は "bot" を含まない
// Google-InspectionTool を使うため個別列挙が必要 (参照: https://developers.google.com/search/docs/crawling-indexing/google-common-crawlers)。
const CRAWLER_USER_AGENT_RE =
  /bot|crawl|spider|slurp|facebookexternalhit|whatsapp|telegram|google-inspectiontool|googleother|mediapartners-google|apis-google/i;

function isCrawlerUserAgent(): boolean {
  if (typeof window === "undefined") return false;
  return CRAWLER_USER_AGENT_RE.test(navigator.userAgent);
}

function detectBrowser(): Browser {
  if (typeof window === "undefined") return "unknown";

  const userAgent = navigator.userAgent.toLowerCase();

  // Order matters - check more specific browsers first
  if (userAgent.includes("samsungbrowser")) return "samsung";
  if (userAgent.includes("edg/") || userAgent.includes("edge/")) return "edge";
  if (userAgent.includes("opr/") || userAgent.includes("opera")) return "opera";
  if (
    userAgent.includes("chrome") &&
    !userAgent.includes("edg") &&
    !userAgent.includes("opr")
  )
    return "chrome";
  if (userAgent.includes("firefox") || userAgent.includes("fxios"))
    return "firefox";
  if (
    userAgent.includes("safari") &&
    !userAgent.includes("chrome") &&
    !userAgent.includes("crios")
  )
    return "safari";

  return "unknown";
}

type SupportedLanguage = "en" | "ko" | "zh-Hant" | "zh-Hans";

// Translation content
const translations = {
  en: {
    title: "Translation Available",
    description: "This page is in Japanese. Here's how to translate it in",
    stepsTitle: "Translation Steps:",
    unknownBrowserWarning:
      "We couldn't detect your browser. For the best translation experience, we recommend using Chrome, Safari, or Edge.",
    mobileFirefoxWarning:
      "on mobile doesn't have built-in translation. Consider using Chrome or Safari for automatic translation.",
    tipTitle:
      "Tip: If some content still appears in Japanese after translation",
    tipSteps: [
      "Turn off the translation temporarily",
      "Re-enable translation",
      "This helps the browser detect and translate all content properly",
    ],
    gotItButton: "Got it",
    browserInstructions: {
      chrome: {
        desktop: [
          "Right-click anywhere on the page",
          'Select "Translate to [Your Language]"',
          "Or click the translate icon in the address bar if it appears",
          "If translation doesn't appear, go to Settings → Languages → Use Google Translate",
        ],
        mobile: [
          "Tap the three dots menu (⋮) in the top right",
          'Select "Translate..."',
          "Choose your preferred language",
          'If not available, tap the three dots menu (⋮) → Customize Menu and enable "Translate"',
        ],
      },
      safari: {
        desktop: [
          "Click the translate icon in the address bar",
          "Select your preferred language from the dropdown",
          "Or go to Safari → Settings → Websites → Translation",
          "Enable translation for this website",
        ],
        mobile: [
          "Tap the translate icon in the address bar",
          'Select "Translate Website..."',
          "If not available, go to Settings → Safari → Translation",
        ],
      },
      firefox: {
        desktop: [
          "Click the translate icon in the address bar if it appears",
          'Or install the "To Google Translate" extension from Firefox Add-ons',
          'Right-click on the page and select "Translate This Page"',
          "Choose your preferred language",
        ],
        mobile: [
          "Firefox mobile doesn't have built-in translation",
          "Open the page in Chrome or Safari for translation",
          "Or use Google Translate app with screen translation",
        ],
      },
      edge: {
        desktop: [
          "Click the translate icon in the address bar",
          "Choose your preferred language",
          "Or right-click and select Translate",
          "If not available, go to Settings → Languages",
        ],
        mobile: [
          "Tap the three dots menu (...) at the bottom",
          'Select "Translate"',
          "Choose your preferred language",
        ],
      },
      opera: {
        desktop: [
          "Right-click anywhere on the page",
          'Select "Translate..."',
          "Choose your preferred language",
          "Or check the address bar for translate icon",
        ],
        mobile: [
          "Opera mobile doesn't have built-in translation",
          "Open the page in Chrome or Safari for translation",
          "Or use Google Translate app",
        ],
      },
      samsung: {
        desktop: [
          "Tap the menu button",
          'Look for "Translate" option',
          "Choose your preferred language",
        ],
        mobile: [
          "Tap the menu button (three lines)",
          'Select "Translate"',
          "Choose your preferred language",
        ],
      },
      unknown: {
        desktop: [
          "Look for a translate icon in your address bar",
          "Check your browser menu for translation options",
          "Or right-click on the page to see translation options",
        ],
        mobile: [
          "Check your browser menu for translation options",
          "Or open this page in Chrome or Safari",
          "You can also use Google Translate app",
          "Take a screenshot and use image translation features",
        ],
      },
    },
  },
  ko: {
    title: "번역 기능 사용 가능",
    description:
      "이 페이지는 일본어로 되어 있습니다. 다음과 같이 번역할 수 있습니다",
    stepsTitle: "번역 단계:",
    unknownBrowserWarning:
      "브라우저를 감지할 수 없습니다. 최상의 번역 경험을 위해 Chrome, Safari 또는 Edge 사용을 권장합니다.",
    mobileFirefoxWarning:
      "모바일에서는 내장 번역 기능이 없습니다. 자동 번역을 위해 Chrome 또는 Safari 사용을 고려해보세요.",
    tipTitle: "팁: 번역 후에도 일부 내용이 일본어로 나타나는 경우",
    tipSteps: [
      "번역을 일시적으로 끄기",
      "번역 다시 활성화",
      "이렇게 하면 브라우저가 모든 내용을 제대로 감지하고 번역할 수 있습니다",
    ],
    gotItButton: "알겠습니다",
    browserInstructions: {
      chrome: {
        desktop: [
          "페이지의 아무 곳이나 마우스 오른쪽 버튼 클릭",
          '"한국어로 번역" 선택',
          "또는 주소 표시줄에 번역 아이콘이 나타나면 클릭",
          "번역이 나타나지 않으면 설정 → 언어 → Google 번역 사용으로 이동",
        ],
        mobile: [
          "오른쪽 상단의 점 세 개 메뉴(⋮) 탭",
          '"번역..." 선택',
          "선호하는 언어 선택",
          '"번역" 옵션이 없다면 점 세 개 메뉴(⋮) → 메뉴 사용자 지정에서 "번역" 활성화',
        ],
      },
      safari: {
        desktop: [
          "주소 표시줄에 있는 번역 아이콘 클릭",
          "드롭다운에서 선호하는 언어 선택",
          "또는 Safari → 설정 → 웹사이트 → 번역으로 이동",
          "이 웹사이트에 대한 번역 활성화",
        ],
        mobile: [
          "주소 표시줄에 있는 번역 아이콘 탭",
          '"웹사이트 번역..." 선택',
          "사용할 수 없다면 설정 → Safari → 번역으로 이동",
        ],
      },
      firefox: {
        desktop: [
          "주소 표시줄에 번역 아이콘이 나타나면 클릭",
          'Firefox 추가 기능에서 "To Google Translate" 확장 프로그램 설치',
          '페이지에 마우스 오른쪽 버튼 클릭 후 "이 페이지 번역" 선택',
          "선호하는 언어 선택",
        ],
        mobile: [
          "Firefox 모바일에는 내장 번역 기능이 없습니다",
          "번역을 위해 Chrome 또는 Safari로 페이지를 여세요",
          "또는 Google 번역 앱의 화면 번역 기능을 이용하세요",
        ],
      },
      edge: {
        desktop: [
          "주소 표시줄에 있는 번역 아이콘 클릭",
          "선호하는 언어 선택",
          "또는 마우스 오른쪽 버튼 클릭 후 번역 선택",
          "사용할 수 없다면 설정 → 언어로 이동",
        ],
        mobile: [
          "하단의 점 세 개 메뉴(...) 탭",
          '"번역" 선택',
          "선호하는 언어 선택",
        ],
      },
      opera: {
        desktop: [
          "페이지의 아무 곳이나 마우스 오른쪽 버튼 클릭",
          '"번역..." 선택',
          "선호하는 언어 선택",
          "또는 주소 표시줄에 번역 아이콘이 있는지 확인",
        ],
        mobile: [
          "Opera 모바일에는 내장 번역 기능이 없습니다",
          "번역을 위해 Chrome 또는 Safari로 페이지를 여세요",
          "또는 Google 번역 앱을 사용하세요",
        ],
      },
      samsung: {
        desktop: ["메뉴 버튼 탭", '"번역" 옵션 찾기', "선호하는 언어 선택"],
        mobile: ["메뉴 버튼(세 줄) 탭", '"번역" 선택', "선호하는 언어 선택"],
      },
      unknown: {
        desktop: [
          "주소 표시줄에서 번역 아이콘을 찾아보세요",
          "브라우저 메뉴에서 번역 옵션 확인",
          "또는 페이지에 마우스 오른쪽 버튼 클릭으로 번역 옵션 확인",
        ],
        mobile: [
          "브라우저 메뉴에서 번역 옵션 확인",
          "또는 Chrome이나 Safari로 이 페이지를 여세요",
          "Google 번역 앱도 사용할 수 있습니다",
          "스크린샷을 찍고 이미지 번역 기능을 이용하세요",
        ],
      },
    },
  },
  "zh-Hant": {
    title: "翻譯功能可用",
    description: "此頁面為日文。您可以通過以下方式翻譯",
    stepsTitle: "翻譯步驟：",
    unknownBrowserWarning:
      "無法檢測您的瀏覽器。為獲得最佳翻譯體驗，建議使用 Chrome、Safari 或 Edge。",
    mobileFirefoxWarning:
      "在手機上沒有內建翻譯功能。請考慮使用 Chrome 或 Safari 進行自動翻譯。",
    tipTitle: "提示：如果翻譯後仍有部分內容顯示為日文",
    tipSteps: [
      "暫時關閉翻譯",
      "重新啟用翻譯",
      "這樣可以幫助瀏覽器正確檢測並翻譯所有內容",
    ],
    gotItButton: "我知道了",
    browserInstructions: {
      chrome: {
        desktop: [
          "在頁面上任意地方按右鍵",
          "選擇「翻譯為中文」",
          "或點擊位址欄中的翻譯圖示（如果出現）",
          "如果翻譯選項未出現，請來到設定 → 語言 → 使用 Google 翻譯",
        ],
        mobile: [
          "點擊右上角的三個點選單（⋮）",
          "選擇「翻譯...」",
          "選擇您偏好的語言",
          "如果沒有此選項，點擊三個點選單（⋮）→ 自訂選單並啟用「翻譯」",
        ],
      },
      safari: {
        desktop: [
          "點擊位址欄中的翻譯圖示",
          "從下拉式選單中選擇您偏好的語言",
          "或前往 Safari → 設定 → 網站 → 翻譯",
          "為此網站啟用翻譯功能",
        ],
        mobile: [
          "點擊位址欄中的翻譯圖示",
          "選擇「翻譯網站...」",
          "如果沒有此選項，前往設定 → Safari → 翻譯",
        ],
      },
      firefox: {
        desktop: [
          "如果位址欄中出現翻譯圖示，請點擊它",
          "或從 Firefox 附加元件安裝「To Google Translate」擴充功能",
          "在頁面上按右鍵並選擇「翻譯此頁面」",
          "選擇您偏好的語言",
        ],
        mobile: [
          "Firefox 手機版沒有內建翻譯功能",
          "請在 Chrome 或 Safari 中開啟此頁面進行翻譯",
          "或使用 Google 翻譯應用程式的螢幕翻譯功能",
        ],
      },
      edge: {
        desktop: [
          "點擊位址欄中的翻譯圖示",
          "選擇您偏好的語言",
          "或按右鍵並選擇翻譯",
          "如果沒有此選項，前往設定 → 語言",
        ],
        mobile: [
          "點擊底部的三個點選單（...）",
          "選擇「翻譯」",
          "選擇您偏好的語言",
        ],
      },
      opera: {
        desktop: [
          "在頁面上任意地方按右鍵",
          "選擇「翻譯...」",
          "選擇您偏好的語言",
          "或檢查位址欄是否有翻譯圖示",
        ],
        mobile: [
          "Opera 手機版沒有內建翻譯功能",
          "請在 Chrome 或 Safari 中開啟此頁面進行翻譯",
          "或使用 Google 翻譯應用程式",
        ],
      },
      samsung: {
        desktop: ["點擊選單按鈕", "尋找「翻譯」選項", "選擇您偏好的語言"],
        mobile: ["點擊選單按鈕（三條線）", "選擇「翻譯」", "選擇您偏好的語言"],
      },
      unknown: {
        desktop: [
          "在位址欄中尋找翻譯圖示",
          "檢查瀏覽器選單中的翻譯選項",
          "或在頁面上按右鍵查看翻譯選項",
        ],
        mobile: [
          "檢查瀏覽器選單中的翻譯選項",
          "或在 Chrome 或 Safari 中開啟此頁面",
          "您也可以使用 Google 翻譯應用程式",
          "截取螢幕截圖並使用圖片翻譯功能",
        ],
      },
    },
  },
  "zh-Hans": {
    title: "翻译功能可用",
    description: "此页面为日文。您可以通过以下方式翻译",
    stepsTitle: "翻译步骤：",
    unknownBrowserWarning:
      "无法检测您的浏览器。为获得最佳翻译体验，建议使用 Chrome、Safari 或 Edge。",
    mobileFirefoxWarning:
      "在手机上没有内建翻译功能。请考虑使用 Chrome 或 Safari 进行自动翻译。",
    tipTitle: "提示：如果翻译后仍有部分内容显示为日文",
    tipSteps: [
      "暂时关闭翻译",
      "重新启用翻译",
      "这样可以帮助浏览器正确检测并翻译所有内容",
    ],
    gotItButton: "我知道了",
    browserInstructions: {
      chrome: {
        desktop: [
          "右键点击页面任意位置",
          '选择"翻译为中文"',
          "或点击地址栏中的翻译图标（如果出现）",
          "如果翻译选项未出现，请前往设置 → 语言 → 使用 Google 翻译",
        ],
        mobile: [
          "点击右上角的三个点菜单（⋮）",
          '选择"翻译..."',
          "选择您偏好的语言",
          '如果没有此选项，点击三个点菜单（⋮）→ 自定义菜单并启用"翻译"',
        ],
      },
      safari: {
        desktop: [
          "点击地址栏中的翻译图标",
          "从下拉菜单中选择您偏好的语言",
          "或前往 Safari → 设置 → 网站 → 翻译",
          "为此网站启用翻译功能",
        ],
        mobile: [
          "点击地址栏中的翻译图标",
          '选择"翻译网站..."',
          "如果没有此选项，前往设置 → Safari → 翻译",
        ],
      },
      firefox: {
        desktop: [
          "如果地址栏中出现翻译图标，请点击它",
          '或从 Firefox 附加组件安装"To Google Translate"扩展程序',
          '右键点击页面并选择"翻译此页面"',
          "选择您偏好的语言",
        ],
        mobile: [
          "Firefox 手机版没有内建翻译功能",
          "请在 Chrome 或 Safari 中打开此页面进行翻译",
          "或使用 Google 翻译应用程序的屏幕翻译功能",
        ],
      },
      edge: {
        desktop: [
          "点击地址栏中的翻译图标",
          "选择您偏好的语言",
          "或右键点击并选择翻译",
          "如果没有此选项，前往设置 → 语言",
        ],
        mobile: [
          "点击底部的三个点菜单（...）",
          '选择"翻译"',
          "选择您偏好的语言",
        ],
      },
      opera: {
        desktop: [
          "右键点击页面任意位置",
          '选择"翻译..."',
          "选择您偏好的语言",
          "或检查地址栏是否有翻译图标",
        ],
        mobile: [
          "Opera 手机版没有内建翻译功能",
          "请在 Chrome 或 Safari 中打开此页面进行翻译",
          "或使用 Google 翻译应用程序",
        ],
      },
      samsung: {
        desktop: ["点击菜单按钮", '寻找"翻译"选项', "选择您偏好的语言"],
        mobile: ["点击菜单按钮（三条线）", '选择"翻译"', "选择您偏好的语言"],
      },
      unknown: {
        desktop: [
          "在地址栏中寻找翻译图标",
          "检查浏览器菜单中的翻译选项",
          "或右键点击页面查看翻译选项",
        ],
        mobile: [
          "检查浏览器菜单中的翻译选项",
          "或在 Chrome 或 Safari 中打开此页面",
          "您也可以使用 Google 翻译应用程序",
          "截取屏幕截图并使用图片翻译功能",
        ],
      },
    },
  },
};

// Hook to get user's preferred language for translation guide
export function useTranslationLanguage(): SupportedLanguage | null {
  const [language, setLanguage] = useState<SupportedLanguage | null>(null);

  useEffect(() => {
    // クローラーには翻訳案内を出さない
    if (isCrawlerUserAgent()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Client-side crawler detection
      setLanguage(null);
      return;
    }

    const browserLang = (
      navigator.language ||
      navigator.languages?.[0] ||
      ""
    ).toLowerCase();

    // Return null for Japanese (no translation guide needed)
    if (browserLang.startsWith("ja")) {
      setLanguage(null);
      return;
    }

    // Map browser languages to supported languages
    if (browserLang.startsWith("ko")) {
      setLanguage("ko");
    } else if (
      browserLang === "zh-tw" ||
      browserLang === "zh-hk" ||
      browserLang === "zh-mo" ||
      browserLang.startsWith("zh-hant")
    ) {
      setLanguage("zh-Hant");
    } else if (
      browserLang === "zh-cn" ||
      browserLang === "zh-sg" ||
      browserLang.startsWith("zh-hans")
    ) {
      setLanguage("zh-Hans");
    } else {
      // Default to English for all other languages
      setLanguage("en");
    }
  }, []);

  return language;
}

export function TranslationGuideDialog({
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const pathname = usePathname();
  const platform = useMemo(() => detectPlatform(), []);
  const browser = useMemo(() => detectBrowser(), []);
  const language = useTranslationLanguage();

  // Use controlled state if provided, otherwise use internal state
  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;

  useEffect(() => {
    // Skip auto-open logic if component is controlled
    if (controlledIsOpen !== undefined) return;

    // Don't show dialog if language is null (Japanese) or if language detection hasn't completed
    if (!language) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Sync dialog state with language
      setInternalIsOpen(false);
      return;
    }

    let dismissedTimestamp: string | null = null;
    try {
      dismissedTimestamp = localStorage.getItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable (sandboxed iframe, ITP, iOS Lockdown Mode, etc.)
      // Fall through: treat as not dismissed
    }
    if (dismissedTimestamp) {
      const dismissedAt = parseInt(dismissedTimestamp, 10);
      // eslint-disable-next-line @sa-nu/no-date
      const now = Date.now();
      if (now - dismissedAt < DISMISS_DURATION_MS) {
        setInternalIsOpen(false);
        return;
      }
    }

    // Add a 3 second delay after first paint
    const timer = setTimeout(() => {
      setInternalIsOpen(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [pathname, controlledIsOpen, language]);

  const handleDismiss = () => {
    try {
      // eslint-disable-next-line @sa-nu/no-date
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      // localStorage unavailable — dismiss is session-only, dialog won't reappear this load
    }
    setIsOpen(false);
  };

  const getTranslationInstructions = () => {
    if (!language) return [];

    const t = translations[language];
    const browserInstructions = t.browserInstructions[browser];
    const platformInstructions =
      browserInstructions?.[platform] ||
      t.browserInstructions.unknown[platform];
    const instructions =
      platformInstructions || t.browserInstructions.unknown.desktop!;

    // Add icons for specific browsers and platforms
    const translateIcon = (
      <svg
        className="inline h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
        />
      </svg>
    );

    const menuIcon = (
      <svg className="inline h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M3 4h18v2H3V4zm0 14h18v2H3v-2zm3-7h12c.55 0 1-.45 1-1s-.45-1-1-1H6c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h8c.55 0 1-.45 1-1s-.45-1-1-1H6c-.55 0-1 .45-1 1s.45 1 1 1z" />
      </svg>
    );

    // Transform string instructions to include icons where appropriate
    return instructions.map((instruction, index) => {
      // Add icons for Chrome desktop third instruction (translate icon in address bar)
      if (browser === "chrome" && platform === "desktop" && index === 2) {
        const iconText =
          language === "en"
            ? "translate icon"
            : language === "ko"
              ? "번역 아이콘"
              : language === "zh-Hant"
                ? "翻譯圖示"
                : "翻译图标";

        const beforeText =
          language === "en"
            ? "Or click the "
            : language === "ko"
              ? "또는 주소 표시줄에 "
              : language === "zh-Hant"
                ? "或點擊位址欄中的"
                : "或点击地址栏中的";

        const afterText =
          language === "en"
            ? " in the address bar if it appears"
            : language === "ko"
              ? "이 나타나면 클릭"
              : language === "zh-Hant"
                ? "（如果出現）"
                : "（如果出现）";

        return (
          <span key={`${browser}-${platform}-icon-${index}`}>
            {beforeText}
            {iconText} {translateIcon} {afterText}
          </span>
        );
      }

      // Add icons for Safari desktop first instruction (translate icon in address bar)
      if (browser === "safari" && platform === "desktop" && index === 0) {
        const beforeText =
          language === "en"
            ? "Click the "
            : language === "ko"
              ? ""
              : language === "zh-Hant"
                ? ""
                : "";

        const iconText =
          language === "en"
            ? "translate icon"
            : language === "ko"
              ? "번역 아이콘"
              : language === "zh-Hant"
                ? "翻譯圖示"
                : "翻译图标";

        const afterText =
          language === "en"
            ? " in the address bar"
            : language === "ko"
              ? ""
              : language === "zh-Hant"
                ? ""
                : "";

        if (language === "en") {
          return (
            <span key={`${browser}-${platform}-icon-${index}`}>
              {beforeText}
              {iconText} {translateIcon} {afterText}
            </span>
          );
        }
      }

      // Add icons for Safari mobile first instruction (translate icon)
      if (browser === "safari" && platform === "mobile" && index === 0) {
        if (language === "en") {
          return (
            <span key={`${browser}-${platform}-icon-${index}`}>
              Tap the {menuIcon} icon in the address bar
            </span>
          );
        }
      }

      // Add icons for Firefox desktop first instruction (translate icon in address bar)
      if (browser === "firefox" && platform === "desktop" && index === 0) {
        const beforeText =
          language === "en"
            ? "Click the "
            : language === "ko"
              ? ""
              : language === "zh-Hant"
                ? ""
                : "";

        const iconText =
          language === "en"
            ? "translate icon"
            : language === "ko"
              ? "번역 아이콘"
              : language === "zh-Hant"
                ? "翻譯圖示"
                : "翻译图标";

        const afterText =
          language === "en"
            ? " in the address bar if it appears"
            : language === "ko"
              ? "이 나타나면 클릭"
              : language === "zh-Hant"
                ? "如果出現，請點擊它"
                : "如果出现，请点击它";

        if (language === "en") {
          return (
            <span key={`${browser}-${platform}-icon-${index}`}>
              {beforeText}
              {iconText} {translateIcon} {afterText}
            </span>
          );
        }
      }

      // Add icons for Edge desktop first instruction (translate icon in address bar)
      if (browser === "edge" && platform === "desktop" && index === 0) {
        const beforeText =
          language === "en"
            ? "Click the "
            : language === "ko"
              ? ""
              : language === "zh-Hant"
                ? ""
                : "";

        const iconText =
          language === "en"
            ? "translate icon"
            : language === "ko"
              ? "번역 아이콘"
              : language === "zh-Hant"
                ? "翻譯圖示"
                : "翻译图标";

        const afterText =
          language === "en"
            ? " in the address bar"
            : language === "ko"
              ? ""
              : language === "zh-Hant"
                ? ""
                : "";

        if (language === "en") {
          return (
            <span key={`${browser}-${platform}-icon-${index}`}>
              {beforeText}
              {iconText} {translateIcon} {afterText}
            </span>
          );
        }
      }

      // Add icons for Opera desktop first instruction (translate icon in address bar)
      if (browser === "opera" && platform === "desktop" && index === 0) {
        const beforeText =
          language === "en"
            ? "Click the "
            : language === "ko"
              ? ""
              : language === "zh-Hant"
                ? ""
                : "";

        const iconText =
          language === "en"
            ? "translate icon"
            : language === "ko"
              ? "번역 아이콘"
              : language === "zh-Hant"
                ? "翻譯圖示"
                : "翻译图标";

        const afterText =
          language === "en"
            ? " in the address bar if it appears"
            : language === "ko"
              ? "이 있는지 확인"
              : language === "zh-Hant"
                ? "是否有翻譯圖示"
                : "是否有翻译图标";

        if (language === "en") {
          return (
            <span key={`${browser}-${platform}-icon-${index}`}>
              {beforeText}
              {iconText} {translateIcon} {afterText}
            </span>
          );
        }
      }

      // Add icons for Unknown browser first instruction (translate icon)
      if (browser === "unknown" && platform === "desktop" && index === 0) {
        const beforeText =
          language === "en"
            ? "Look for a "
            : language === "ko"
              ? ""
              : language === "zh-Hant"
                ? ""
                : "";

        const iconText =
          language === "en"
            ? "translate icon"
            : language === "ko"
              ? "번역 아이콘을"
              : language === "zh-Hant"
                ? "翻譯圖示"
                : "翻译图标";

        const afterText =
          language === "en"
            ? " in the address bar"
            : language === "ko"
              ? " 찾아보세요"
              : language === "zh-Hant"
                ? ""
                : "";

        if (language === "en") {
          return (
            <span key={`${browser}-${platform}-icon-${index}`}>
              {beforeText}
              {iconText} {translateIcon} {afterText}
            </span>
          );
        }
      }

      // Return original string instruction for all other cases
      return instruction;
    });
  };

  const getBrowserName = () => {
    const names: Record<Browser, string> = {
      chrome: "Chrome",
      safari: "Safari",
      firefox: "Firefox",
      edge: "Edge",
      opera: "Opera",
      samsung: "Samsung Browser",
      unknown: "Your Browser",
    };
    return names[browser];
  };

  if (!isOpen || !language) return null;

  const instructions = getTranslationInstructions();
  const browserName = getBrowserName();
  const t = translations[language];

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-[90vw] max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg">
          <Dialog.Title className="text-skogecha mb-[16px] text-lg font-bold">
            {t.title}
          </Dialog.Title>
          <Dialog.Description className="mb-[24px] text-sm leading-relaxed text-gray-600">
            {t.description} {browserName} on{" "}
            {platform === "mobile" ? "mobile" : "desktop"}:
          </Dialog.Description>

          <div className="mb-[24px]">
            <h4 className="text-skogecha mb-[12px] font-semibold">
              {browserName} {t.stepsTitle}
            </h4>
            <ol className="list-decimal space-y-[8px] pl-5 text-sm text-gray-600">
              {instructions.map((instruction, index) => (
                <li key={index} className="leading-relaxed">
                  {typeof instruction === "string" ? instruction : instruction}
                </li>
              ))}
            </ol>
          </div>

          {browser === "unknown" && (
            <div className="mb-[16px] rounded-md bg-amber-50 p-[12px]">
              <p className="text-xs text-amber-800">
                {t.unknownBrowserWarning}
              </p>
            </div>
          )}

          {(browser === "firefox" || browser === "opera") &&
            platform === "mobile" && (
              <div className="mb-[16px] rounded-md bg-blue-50 p-[12px]">
                <p className="text-xs text-blue-800">
                  {browserName} {t.mobileFirefoxWarning}
                </p>
              </div>
            )}

          <div className="mb-[16px] rounded-md border border-yellow-200 bg-yellow-50 p-[12px]">
            <div className="flex items-start">
              <svg
                className="mt-[2px] mr-[8px] h-5 w-5 shrink-0 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="flex-1">
                <p className="mb-[4px] text-xs font-semibold text-yellow-800">
                  {t.tipTitle}
                </p>
                <ol className="list-inside list-decimal space-y-[4px] text-xs text-yellow-700">
                  {t.tipSteps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Dialog.Close asChild>
              <button
                // 閉じるだけで、押されなかったことに情報が無い
                data-analytics-ignore
                onClick={handleDismiss}
                className="bg-sbeige text-skogecha rounded-full px-[24px] py-[8px] text-sm font-medium hover:bg-[#e6e0c6]"
              >
                {t.gotItButton}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Floating Translation Button Component
export function FloatingTranslationButton() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const language = useTranslationLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show button after a short delay to avoid layout shift
    const timer = setTimeout(() => {
      setIsVisible(language !== null);
    }, 500);
    return () => clearTimeout(timer);
  }, [language]);

  if (!isVisible) return null;

  return (
    <>
      <button
        data-analytics-id="translation_guide_opened_guide"
        onClick={() => setIsDialogOpen(true)}
        className="bg-sbeige focus:ring-skogecha fixed right-[20px] bottom-[80px] z-40 flex h-[48px] w-[48px] items-center justify-center rounded-full shadow-lg transition-all hover:scale-110 hover:bg-[#e6e0c6] focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
        aria-label="Open translation guide"
        title="Translation Guide"
      >
        <svg
          className="text-skogecha h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
          />
        </svg>
      </button>

      <TranslationGuideDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </>
  );
}
